import { type ComponentType, createElement, lazy } from 'react'

// React 의 lazy 와 같은 제약을 쓴다. 좁히면 정적 프로퍼티를 가진 페이지 컴포넌트 타입이 걸러진다.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LoadableComponent = ComponentType<any>

/**
 * 브라우저별로 동적 import 실패 메시지가 다르다. 하나라도 빠지면 그 엔진에서는 복구가 동작하지 않는다.
 */
const CHUNK_LOAD_ERROR_PATTERNS = [
  /Importing a module script failed/, // WebKit
  /Failed to fetch dynamically imported module/, // Blink
  /error loading dynamically imported module/, // Gecko
  /Unable to preload CSS/, // Vite preload helper
]

const RELOAD_MARK_KEY = 'CHUNK_RECOVERY_RELOADED_AT'
const RELOAD_COOLDOWN_MS = 30_000
const RETRY_DELAYS_MS = [300, 800]

const isChunkLoadError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error)
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(message))
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// search 는 비교하지 않는다 — 쿼리로 탭·필터를 관리하는 화면이 많아, 같은 화면에 머무르는데도
// "다른 화면으로 이동했다"고 오판하게 된다.
const currentRoute = () => window.location.pathname

// 사파리 프라이빗 브라우징 등에서는 sessionStorage 접근 자체가 실패한다.
const readReloadMark = () => {
  try {
    return Number(sessionStorage.getItem(RELOAD_MARK_KEY)) || 0
  } catch {
    return 0
  }
}

// 쿨다운을 기록하지 못하면 리로드 1회 제한이 성립하지 않는다. 성공 여부를 돌려주어 호출부가
// 리로드를 포기할 수 있게 한다 — 기록 없이 리로드하면 실패가 계속될 때 새로고침 루프가 된다.
const markReloadAttempt = (at: number) => {
  try {
    sessionStorage.setItem(RELOAD_MARK_KEY, String(at))
    return true
  } catch {
    return false
  }
}

/**
 * 동적 import 가 실패했을 때 화면 크래시로 번지지 않게 복구를 시도한다.
 *
 * 재시도만으로는 부족하다 — WebKit 은 실패한 모듈 스크립트를 모듈 맵에 실패 상태로 기록해서
 * 같은 URL 로 다시 import 해도 네트워크를 타지 않고 즉시 같은 에러를 돌려준다. 그래서 재시도가
 * 소진되면 문서를 새로 띄우는 것이 유일한 복구 경로다.
 */
export const loadModuleWithRecovery = async <T extends LoadableComponent>(
  load: () => Promise<{ default: T }>,
): Promise<{ default: T }> => {
  const routeAtStart = currentRoute()

  for (let attempt = 0; ; attempt += 1) {
    // 전파해야 하는 실패는 새 예외를 만들지 않고 이 프로미스를 그대로 돌려준다.
    const pending = load()

    try {
      return await pending
    } catch (error) {
      // 청크 로드 실패가 아니면 개입하지 않는다. 삼키면 진짜 버그가 묻힌다.
      if (!isChunkLoadError(error)) return pending

      // 로드 중 화면이 바뀌었다면 이 청크는 지금 쓰이지 않는다. 크래시 화면도 리로드도 띄우지 않는다.
      //
      // 이때 빈 컴포넌트로 resolve 하면 안 된다 — React.lazy 는 resolve 된 payload 를 컴포넌트에
      // 영구 메모이즈(`_status: Resolved`)하므로, 나중에 같은 화면을 다시 열어도 ctor 가 재실행되지
      // 않아 무증상 빈 화면으로 굳는다. 렌더 시점에 ctor 가 돌 새 lazy 를 심어두면, 지금은 아무
      // 일도 하지 않고 다시 열릴 때 복구를 처음부터 재시도한다.
      //
      // 그 lazy 를 payload 의 default 에 그대로 넣으면 안 된다. React.lazy 는 컴포넌트가 아니라
      // 객체를 돌려주고, React 는 resolve 된 default 가 class·function 이길 요구해 #306 을 던진다.
      // 함수 컴포넌트로 감싸 유효한 element type 으로 만든다.
      if (currentRoute() !== routeAtStart) {
        const Deferred = lazy(() => loadModuleWithRecovery(load))
        return { default: ((props) => createElement(Deferred, props)) as unknown as T }
      }

      const nextDelay = RETRY_DELAYS_MS[attempt]
      if (nextDelay !== undefined) {
        await wait(nextDelay)
        continue
      }

      const now = Date.now()
      if (now - readReloadMark() > RELOAD_COOLDOWN_MS && markReloadAttempt(now)) {
        window.location.reload()
        // 리로드가 시작되면 이 화면은 버려진다. resolve 하지 않아 폴백을 유지한다.
        return new Promise<{ default: T }>(() => undefined)
      }

      // 리로드로도 복구되지 않았거나 쿨다운을 기록할 수 없다. ErrorBoundary 가 받아 안내 화면을 띄운다.
      return pending
    }
  }
}

/**
 * `React.lazy` 드롭인 대체. 호출부는 그대로 두고 import 경로만 바꿔 쓴다.
 */
export const lazyWithRecovery = <T extends LoadableComponent>(
  load: () => Promise<{ default: T }>,
) => lazy(() => loadModuleWithRecovery(load))
